/**
 * LEIActionPlanCard — hybrid action-plan surface for LEI pillars.
 *
 * Mirror of `MaturityActionPlanCard` but wired to
 * `/api/lei-action-plan`. Same UX contract:
 *
 *   1. AI coaching paragraph at the top (Claude Sonnet 4.5,
 *      personalized to the org's current LEI pillar score +
 *      overall LEI).
 *   2. Three static playbook anchors below — render even when
 *      the LLM is rate-limited so the user always gets something
 *      actionable.
 *   3. "Pin to Quarterly Deck" saves the plan as a `lei-action`
 *      themed pin so the home page Coaching Commitments tile can
 *      pick it up alongside maturity pins.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Sparkles, ClipboardCheck, RefreshCw, AlertTriangle, Loader2, Calendar, User, Pin, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import logger from '../../lib/logger';

// Pin description is capped at 400 chars by the backend Pin
// schema — trim the AI paragraph (typically ~550 chars) to fit.
const PIN_DESCRIPTION_LIMIT = 400;
const buildPinDescription = (data) => {
  const ai = data?.ai_context?.text;
  const fallback = `Action plan for ${data?.pillar_name || 'your weakest LEI pillar'} (score ${data?.pillar_score}/100).`;
  const text = ai && ai.trim() ? ai : fallback;
  if (text.length <= PIN_DESCRIPTION_LIMIT) return text;
  return text.slice(0, PIN_DESCRIPTION_LIMIT - 1).trimEnd() + '…';
};

const buildPinStats = (data) => {
  const stats = [];
  if (typeof data?.pillar_score === 'number') {
    stats.push({ label: 'Pillar score', value: `${data.pillar_score}/100` });
  }
  if (typeof data?.overall_score === 'number') {
    stats.push({ label: 'Org LEI', value: `${data.overall_score}/100` });
  }
  const firstAction = data?.actions?.[0];
  if (firstAction?.title) {
    stats.push({
      label: 'First move',
      value: firstAction.title.length > 26
        ? firstAction.title.slice(0, 25) + '…'
        : firstAction.title,
      hint: firstAction.horizon ? `Horizon: ${firstAction.horizon}` : undefined,
    });
  }
  return stats;
};

export default function LEIActionPlanCard({ pillarId, scoreOverride, overallScore }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [pinned, setPinned] = useState(false);

  const load = useCallback(async () => {
    if (!pillarId) return;
    setLoading(true);
    setError(null);
    try {
      // Pass the EXACT scores the gauge already shows so the AI
      // coaching narrative + cache key match the gauge (no band drift).
      const params = new URLSearchParams({ pillar_id: pillarId });
      if (scoreOverride != null) params.set('pillar_score', String(Math.round(scoreOverride)));
      if (overallScore != null) params.set('overall_score', String(Math.round(overallScore)));
      const res = await api.get(`/lei-action-plan?${params.toString()}`);
      setData(res.data);
    } catch (e) {
      logger.error('LEI action plan load failed:', e);
      setError(e?.response?.data?.detail || 'Could not load action plan');
    } finally {
      setLoading(false);
    }
  }, [pillarId, scoreOverride, overallScore]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPinned(false); }, [pillarId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const handlePin = async () => {
    if (!data || pinning) return;
    setPinning(true);
    try {
      await api.post('/quarterly-deck/pins', {
        path: '/lei-health',
        title: `LEI Action: ${data.pillar_name || 'weakest pillar'}`,
        description: buildPinDescription(data),
        theme: 'lei-action',
        stats: buildPinStats(data),
        note: data.ai_context?.text
          ? data.ai_context.text.slice(0, 400)
          : null,
      });
      setPinned(true);
      toast.success('Action plan pinned to Quarterly Deck');
    } catch (err) {
      logger.error('LEI action plan pin failed:', err);
      toast.error('Could not pin — try again');
    } finally {
      setPinning(false);
    }
  };

  if (!pillarId) return null;

  return (
    <Card data-testid="lei-action-plan-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardCheck className="h-5 w-5 text-rose-600" />
            Action plan: {data?.pillar_name || 'your weakest LEI pillar'}
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant={pinned ? 'outline' : 'default'}
              size="sm"
              onClick={handlePin}
              disabled={pinning || !data || loading}
              className={pinned
                ? 'text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                : 'text-xs bg-slate-900 hover:bg-slate-800 text-white'
              }
              data-testid="lei-action-plan-pin"
            >
              {pinning ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : pinned ? (
                <Check className="h-3.5 w-3.5 mr-1" />
              ) : (
                <Pin className="h-3.5 w-3.5 mr-1" />
              )}
              {pinned ? 'Pinned to Deck' : 'Pin to Quarterly Deck'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={loading || refreshing}
              className="text-xs"
              data-testid="lei-action-plan-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
        {data && (data.pillar_score != null || scoreOverride != null) && (
          <p className="text-xs text-slate-500 mt-1">
            Score <strong>{Math.round(scoreOverride != null ? scoreOverride : data.pillar_score)}/100</strong>
            {data.overall_score != null && (
              <> · org LEI {data.overall_score}</>
            )}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4" data-testid="lei-action-plan-loading">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading personalized action plan…
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3" data-testid="lei-action-plan-error">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {data && (
          <>
            {data.ai_context?.text ? (
              <div
                className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-md p-4"
                data-testid="lei-action-plan-ai-context"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-3.5 w-3.5 text-violet-600" />
                  <span className="text-[11px] uppercase tracking-wider font-bold text-violet-700">
                    Personalized coaching
                  </span>
                  {data.ai_context.cached && (
                    <Badge variant="outline" className="text-[9px] py-0 px-1.5 text-violet-600 border-violet-300">
                      cached
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-slate-800 leading-relaxed">
                  {data.ai_context.text}
                </p>
              </div>
            ) : data.ai_context_status === 'no_data' ? (
              <div className="text-xs text-slate-500 italic" data-testid="lei-action-plan-no-data">
                Personalized coaching will appear once LEI baseline measures are captured for your org.
              </div>
            ) : (
              <div className="text-xs text-slate-500 italic" data-testid="lei-action-plan-ai-degraded">
                Personalized coaching is temporarily unavailable. Static action plan below is still your best next-step.
              </div>
            )}

            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                Your next 3 moves
              </p>
              <ol className="space-y-2.5" data-testid="lei-action-plan-actions">
                {(data.actions || []).map((a, i) => (
                  <li
                    key={a.title}
                    className="flex gap-3 p-3 rounded-md border border-slate-200 hover:border-slate-300 transition-colors"
                    data-testid={`lei-action-${i + 1}`}
                  >
                    <div className="shrink-0 w-7 h-7 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center justify-center">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{a.title}</p>
                      <p className="text-[12px] text-slate-600 mt-0.5 leading-snug">
                        {a.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[11px] text-slate-500">
                        {a.owner && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {a.owner}
                          </span>
                        )}
                        {a.horizon && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {a.horizon}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
